import { keepPreviousData, queryOptions } from '@tanstack/react-query';

import { FIVE_MINUTES, ONE_MINUTE } from '@/constants/time';
import { paths } from '@/types/api';
import { fetchClient } from '@/util/fetchClient';

export type ImpersonationTargets = NonNullable<
  paths['/v2/impersonation/targets']['get']['responses'][200]['content']['application/json']['data']
>;

export type ImpersonationScope = ImpersonationTargets['scope'];

export type ImpersonationSearchResult = ImpersonationTargets['users'][number];

/** The shortest query the API searches on; anything shorter returns no users. */
export const MIN_IMPERSONATION_QUERY_LENGTH = 2;

/**
 * Fetch who the signed-in staff member may act as.
 *
 * The API always answers for the real caller, even while impersonating, and
 * answers `scope: 'none'` rather than an error for someone who may not
 * impersonate. `q` searches every account, and only does so for `all` scope.
 */
export async function getImpersonationTargetsV2(q?: string) {
  const { data } = await fetchClient.GET('/v2/impersonation/targets', {
    params: { query: q ? { q } : {} },
  });

  return data?.data;
}

/**
 * The caller's scope and support accounts. Enabled only for a session holding
 * a group that could impersonate, so clinicians never make the request.
 */
export function getImpersonationTargetsV2QueryOptions(enabled: boolean) {
  return queryOptions({
    queryKey: ['impersonationTargetsV2'],
    queryFn: () => getImpersonationTargetsV2(),
    staleTime: FIVE_MINUTES,
    enabled,
  });
}

/** Accounts matching `q`, for a caller whose scope is `all`. */
export function searchImpersonationTargetsV2QueryOptions(q: string, enabled: boolean) {
  return queryOptions({
    queryKey: ['impersonationTargetsV2', 'search', q],
    queryFn: () => getImpersonationTargetsV2(q),
    staleTime: ONE_MINUTE,
    placeholderData: keepPreviousData,
    enabled: enabled && q.length >= MIN_IMPERSONATION_QUERY_LENGTH,
  });
}
